import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { SearchHit, SearchMode } from "../lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenNote: (id: string) => void;
}

export function SearchPalette({ open, onClose, onOpenNote }: Props) {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<SearchMode>("hybrid");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setHits([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setHits([]);
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.search(q, mode, 12);
        setHits(r);
        setActive(0);
      } catch {
        setHits([]);
      } finally {
        setBusy(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [q, open, mode]);

  if (!open) return null;

  function openHit(h: SearchHit) {
    onOpenNote(h.id);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-24"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-zinc-800 p-3">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              else if (e.key === "ArrowDown") setActive((i) => Math.min(i + 1, hits.length - 1));
              else if (e.key === "ArrowUp") setActive((i) => Math.max(i - 1, 0));
              else if (e.key === "Enter" && hits[active]) openHit(hits[active]);
            }}
            placeholder="Suche in Notes…"
            className="flex-1 bg-transparent text-sm placeholder-zinc-600 focus:outline-none"
          />
          <div className="flex items-center gap-1 text-[10px]">
            {(["fts", "semantic", "hybrid"] as SearchMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded px-1.5 py-0.5 ${
                  mode === m
                    ? "bg-emerald-700/40 text-emerald-300"
                    : "text-zinc-500 hover:text-zinc-200"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          {busy && <span className="text-xs text-zinc-500">…</span>}
          <kbd className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-500">
            ESC
          </kbd>
        </div>

        <div className="max-h-96 overflow-y-auto p-1">
          {hits.length === 0 && q && !busy && (
            <div className="p-4 text-center text-xs text-zinc-500">Keine Treffer.</div>
          )}
          {hits.map((h, i) => (
            <button
              key={h.id}
              onClick={() => openHit(h)}
              onMouseEnter={() => setActive(i)}
              className={`block w-full rounded px-3 py-2 text-left text-sm ${
                i === active ? "bg-zinc-900 text-white" : "text-zinc-300 hover:bg-zinc-900"
              }`}
            >
              <div className="font-medium">{h.title}</div>
              <div
                className="mt-0.5 truncate text-xs text-zinc-500"
                dangerouslySetInnerHTML={{ __html: h.snippet }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
